import React, { useEffect, useMemo, useState, useCallback } from 'react';
import V4Tabs from './V4Tabs';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';

const DocumentsImportV4 = () => {
  const { getAuthHeaders } = useAuth();
  const headersAuth = useMemo(() => ({ ...getAuthHeaders() }), [getAuthHeaders]);
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('product'); // product | document_link
  const [serverFields, setServerFields] = useState([]);
  const [display, setDisplay] = useState({});
  const [required, setRequired] = useState([]);
  const [parsed, setParsed] = useState({ headers: [], sample: [], total_rows: 0 });
  const [mapping, setMapping] = useState({});
  const [startingJob, setStartingJob] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [activeJobId, setActiveJobId] = useState('');
  const [logModal, setLogModal] = useState({ open: false, job: null, log: '' });
  const [parseError, setParseError] = useState('');
  const [mappingFilter, setMappingFilter] = useState('all'); // all | missing | product | document

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/import/headers`, { headers: headersAuth });
      const data = await res.json();
      setServerFields(data.fields || []);
      setDisplay(data.display || {});
      setRequired(data.required || []);
    })();
  }, [headersAuth]);

  const loadJobs = useCallback(async () => {
    try {
      setJobsLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/v4/documents/import/jobs`, { headers: headersAuth });
      const data = await resp.json();
      if (resp.ok) {
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to load import jobs', err);
    } finally {
      setJobsLoading(false);
    }
  }, [headersAuth]);

  useEffect(() => {
    loadJobs();
    const id = setInterval(loadJobs, 5000);
    return () => clearInterval(id);
  }, [loadJobs]);

  const autoMapFields = useCallback(
    (baseMapping = {}, overrideExisting = false) => {
      if (!parsed.headers.length || !serverFields.length) {
        return { mapping: baseMapping, changed: false };
      }
      const next = overrideExisting ? {} : { ...baseMapping };
      let changed = false;
      const lowerHeaders = (parsed.headers || []).map((h) => h.toLowerCase());
      serverFields.forEach((field) => {
        const key = field.field;
        if (!overrideExisting && next[key]) {
          return;
        }
        const candidates = [
          (display[key] || '').toLowerCase(),
          (key || '').toLowerCase(),
        ].filter(Boolean);
        const matchIndex = lowerHeaders.findIndex((header) =>
          candidates.includes(header)
        );
        if (matchIndex >= 0) {
          const header = parsed.headers[matchIndex];
          next[key] = header;
          changed = true;
        }
      });
      return { mapping: next, changed };
    },
    [parsed.headers, serverFields, display]
  );

  useEffect(() => {
    setMapping((prev) => {
      const { mapping: next, changed } = autoMapFields(prev, false);
      return changed ? next : prev;
    });
  }, [autoMapFields]);

  const onChoose = async (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setParsed({ headers: [], sample: [], total_rows: 0 });
    setMapping({});
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    const h = { ...headersAuth };
    try { delete h['Content-Type']; delete h['content-type']; } catch {}
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/import/parse`, { method: 'POST', headers: h, body: fd });
    if (!res.ok) {
      let txt = '';
      try { const j = await res.json(); txt = j?.detail || JSON.stringify(j); } catch { txt = await res.text(); }
      setParseError(txt || 'Parse failed');
      return;
    }
    const data = await res.json();
    setParsed({ headers: data.headers || [], sample: data.sample || [], total_rows: data.total_rows || 0 });
    setParseError('');
  };

  const handleMappingChange = (target, value) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[target];
      } else {
        next[target] = value;
      }
      return next;
    });
  };

  const handleAutoMapClick = (overrideExisting = false) => {
    setMapping((prev) => {
      const base = overrideExisting ? {} : prev;
      const { mapping: next } = autoMapFields(base, overrideExisting);
      return next;
    });
  };

  const headerSamples = useMemo(() => {
    const sampleRows = parsed.sample || [];
    if (!sampleRows.length) return {};
    const map = {};
    (parsed.headers || []).forEach((header) => {
      const values = sampleRows
        .map((row) => row[header])
        .filter((val) => val !== undefined && val !== null && String(val).trim() !== '');
      map[header] = values.slice(0, 3);
    });
    return map;
  }, [parsed.sample, parsed.headers]);

  const linkOnlyFields = useMemo(
    () => new Set(['product.product_code', 'document.original_filename', 'document.job_id']),
    []
  );

  const effectiveRequired = useMemo(() => {
    if (importMode === 'document_link') {
      return ['product.product_code', 'document.original_filename'];
    }
    return required;
  }, [importMode, required]);

  const requiredMissing = useMemo(
    () => effectiveRequired.filter((field) => !mapping[field]),
    [effectiveRequired, mapping]
  );

  const mappingSummary = useMemo(() => {
    const mappedCount = Object.keys(mapping || {}).length;
    const totalFields = importMode === 'document_link' ? linkOnlyFields.size : serverFields.length;
    return {
      mappedCount,
      totalFields,
      remainingRequired: requiredMissing.length,
    };
  }, [mapping, serverFields.length, requiredMissing.length, importMode, linkOnlyFields]);

  useEffect(() => {
    if (importMode !== 'document_link') {
      return;
    }
    setMapping((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([key, value]) => {
        if (linkOnlyFields.has(key)) {
          next[key] = value;
        }
      });
      return next;
    });
  }, [importMode, linkOnlyFields]);

  const filteredFields = useMemo(() => {
    return serverFields.filter((field) => {
      if (importMode === 'document_link' && !linkOnlyFields.has(field.field)) {
        return false;
      }
      if (mappingFilter === 'missing') {
        return !mapping[field.field];
      }
      if (mappingFilter === 'product') {
        return field.field.startsWith('product.');
      }
      if (mappingFilter === 'document') {
        return field.field.startsWith('document.');
      }
      return true;
    });
  }, [serverFields, mappingFilter, mapping, importMode, linkOnlyFields]);

  const samplePreviewForField = (fieldKey) => {
    const header = mapping[fieldKey];
    if (!header) return null;
    const values = headerSamples[header];
    if (!values || values.length === 0) return null;
    return values.join(', ');
  };

  const fetchJobDetail = useCallback(async (jobId) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v4/documents/import/jobs/${jobId}`, { headers: headersAuth });
      const data = await resp.json();
      if (resp.ok && data.job) {
        setLogModal({ open: true, job: data.job, log: data.job.error_log || '' });
      } else {
        const detail = data?.detail || 'Unable to load job log';
        alert(detail);
      }
    } catch (err) {
      alert(`Unable to load job log: ${err}`);
    }
  }, [headersAuth]);

  const closeLogModal = useCallback(() => {
    setLogModal({ open: false, job: null, log: '' });
  }, []);

  const startImportJob = async () => {
    if (!file) {
      alert('Please select a CSV/XLSX file first.');
      return;
    }
    if (requiredMissing.length) {
      alert(`Map all required fields before importing: ${requiredMissing.map((field) => display[field] || field).join(', ')}`);
      return;
    }
    setStartingJob(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mapping_json', JSON.stringify(mapping));
      fd.append('mode', importMode);
      const h = { ...headersAuth };
      try { delete h['Content-Type']; delete h['content-type']; } catch {}
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/import/jobs`, {
        method: 'POST',
        headers: h,
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || 'Import job failed to start');
      }
      setActiveJobId(data.job_id || '');
      loadJobs();
      alert('Import job queued. Track progress below.');
    } catch (err) {
      alert(err.message || 'Failed to start import job');
    } finally {
      setStartingJob(false);
    }
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">Documents v4</h3>
      <V4Tabs />

      <div className="card">
        <div className="card-body">
          <h6 className="mb-3">Import Product ↔ Document Relations (CSV/XLSX)</h6>
          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <label className="form-label d-block">Import Type</label>
              <div className="btn-group w-100 flex-wrap" role="group" aria-label="Import mode">
                <button
                  type="button"
                  className={`btn btn-outline-primary ${importMode === 'product' ? 'active' : ''}`}
                  onClick={() => setImportMode('product')}
                >
                  Product + Document Fields
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary ${importMode === 'document_link' ? 'active' : ''}`}
                  onClick={() => setImportMode('document_link')}
                >
                  Document Link Only
                </button>
              </div>
              <div className="form-text mt-2">
                {importMode === 'document_link'
                  ? 'Use this mode to upload a lightweight CSV/XLSX that links existing documents (already uploaded/parsed) to products by Product Code + Document Name or Document Job ID.'
                  : 'Use this mode to import or update product and document metadata fields from a complete export file.'}
              </div>
            </div>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Choose CSV/XLSX</label>
              <input type="file" className="form-control" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" onChange={onChoose} />
              <div className="form-text">
                {importMode === 'document_link'
                  ? 'Required: Product Code → product.product_code and Document Name → document.original_filename (or include document.job_id).'
                  : 'Required: map Product Code/Part Number → product.product_code.'}
              </div>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="alert alert-light border w-100 mb-0">
                {importMode === 'document_link'
                  ? 'Link mode does not create new documents; it only connects existing parsed documents to matching products.'
                  : 'Upload the file to preview headers, then use the dropdowns below to map each field. When you start the import it runs as a background job and the status appears in the table at the bottom.'}
              </div>
            </div>
          </div>

          {parseError && (
            <div className="alert alert-danger mt-3" role="alert">
              {parseError}
            </div>
          )}

          {parsed.headers.length > 0 && (
            <div className="mt-4">
              <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
                <div>
                  <div className="fw-semibold">Map import columns</div>
                  <div className="small text-muted">Select the CSV/XLSX header that should populate each database field.</div>
                  {mappingSummary.remainingRequired > 0 && (
                    <div className="badge bg-warning text-dark mt-2">
                      {mappingSummary.remainingRequired} required field{mappingSummary.remainingRequired > 1 ? 's' : ''} missing
                    </div>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <div className="btn-group btn-group-sm" role="group" aria-label="Filter mapping">
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('all')}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'missing' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('missing')}
                    >
                      Missing
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'product' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('product')}
                    >
                      Product
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'document' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('document')}
                    >
                      Document
                    </button>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleAutoMapClick(true)}>
                    Auto map
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setMapping({}); setMappingFilter('all'); }}>
                    Clear selections
                  </button>
                </div>
              </div>

              <div className="row row-cols-1 row-cols-md-2 g-3">
                {filteredFields.map((field) => {
                  const label = display[field.field] || field.field;
                  const isRequired = effectiveRequired.includes(field.field);
                  const missing = !mapping[field.field];
                  const samplePreview = samplePreviewForField(field.field);
                  return (
                    <div className="col" key={field.field}>
                      <div className={`border rounded p-3 h-100 ${missing && isRequired ? 'border-danger' : ''}`}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="fw-semibold">{label} {isRequired && <span className="text-danger">*</span>}</div>
                          <span className="badge bg-light text-muted text-uppercase">{field.field.startsWith('product.') ? 'Product' : 'Document'}</span>
                        </div>
                        <select
                          className="form-select"
                          value={mapping[field.field] || ''}
                          onChange={(e) => handleMappingChange(field.field, e.target.value)}
                        >
                          <option value="">-- Select column --</option>
                          {(parsed.headers || []).map((header) => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </select>
                        <div className="small text-muted mt-2">
                          {field.data_type}{field.max_length ? `, max ${field.max_length}` : ''}
                        </div>
                        <div className="small text-muted mt-1">
                          {mapping[field.field] && (
                            <>
                              <span className="text-uppercase text-secondary">Sample:&nbsp;</span>
                              {samplePreview || 'No sample values'}
                            </>
                          )}
                          {!mapping[field.field] && 'No column selected'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3">
                <button
                  className="btn btn-primary"
                  disabled={startingJob || requiredMissing.length > 0}
                  onClick={startImportJob}
                >
                  {startingJob ? 'Starting Import…' : 'Begin Import'}
                </button>
                {requiredMissing.length > 0 && (
                  <div className="small text-muted mt-2">
                    Map required fields before starting: {requiredMissing.map((field) => display[field] || field).join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}

          {parsed.headers.length > 0 && (
            <div className="mt-3">
              <div className="fw-semibold">Sample rows ({parsed.sample.length} of {parsed.total_rows})</div>
              <div className="table-responsive" style={{ maxHeight: 240 }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      {parsed.headers.map((h, i) => <th key={i} className="small">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.sample.map((r, i) => (
                      <tr key={i}>
                        {parsed.headers.map((h, j) => <td key={j} className="small">{r[h]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card mt-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Recent Import Jobs</h6>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadJobs} disabled={jobsLoading}>
                  {jobsLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>File</th>
                      <th>Mode</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Products (ins / upd)</th>
                      <th>Docs / Links</th>
                      <th>Errors</th>
                      <th>Started</th>
                      <th>Finished</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => {
                      const total = job.total_rows || 0;
                      const processed = job.processed_rows || 0;
                      const pct = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                      const jobMode = job.import_mode === 'document_link' ? 'Document Link' : 'Product';
                      const productStats = job.import_mode === 'document_link' ? '—' : `${job.inserted_products || 0} / ${job.updated_products || 0}`;
                      const docsOrLinks = job.import_mode === 'document_link'
                        ? (job.document_links_upserted || 0)
                        : (job.documents_updated || 0);
                      return (
                        <tr key={job.job_id} className={job.job_id === activeJobId ? 'table-primary' : ''}>
                          <td><code>{job.job_id}</code></td>
                          <td>{job.filename || '—'}</td>
                          <td>{jobMode}</td>
                          <td>
                            <span className={`badge ${
                              job.status === 'completed' ? 'bg-success' :
                              job.status === 'failed' ? 'bg-danger' :
                              job.status === 'running' ? 'bg-warning text-dark' :
                              'bg-secondary'
                            }`}>
                              {job.status}
                            </span>
                          </td>
                          <td style={{ minWidth: 160 }}>
                            {total > 0 ? (
                              <>
                                <div className="small text-muted mb-1">{processed} / {total}</div>
                                <div className="progress" style={{ height: 6 }}>
                                  <div className="progress-bar" style={{ width: `${pct}%` }} />
                                </div>
                              </>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td>{productStats}</td>
                          <td>{docsOrLinks}</td>
                          <td>{job.errors_count || 0}</td>
                          <td>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</td>
                          <td>{job.finished_at ? new Date(job.finished_at).toLocaleString() : '—'}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => fetchJobDetail(job.job_id)}
                            >
                              {job.errors_count ? `View Errors (${job.errors_count})` : 'View Log'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {jobs.length === 0 && (
                      <tr>
                        <td colSpan="11" className="text-center text-muted">No import jobs yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {logModal.open && (
          <>
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Import Job Log</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={closeLogModal} />
                  </div>
                  <div className="modal-body">
                    <div className="small text-muted mb-2">{logModal.job?.job_id}</div>
                    {logModal.log ? (
                      <pre className="bg-light p-3 rounded" style={{ maxHeight: 360, overflow: 'auto' }}>{logModal.log}</pre>
                    ) : (
                      <div className="text-muted">No errors were recorded for this job.</div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeLogModal}>Close</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" onClick={closeLogModal}></div>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentsImportV4;
